/**
 * The passage section of the entity panel: a strip of transcript and the way to its recording.
 *
 * A passage node is a timecode, not an entity, so its panel reads like the tape it was cut
 * from: the times run down a ruled gutter in tabular mono, each turn hangs off the rule with
 * its voice as a small chip and what was said as the body. A voice keeps the colour it has on
 * the recording page, and the name it was given there: the person and their tag, or the role;
 * a voice nobody named is its number. The section has one action: open the recording where the
 * passage starts.
 *
 * Presentational: it is given the view model `passageDetail()` reads from the node's own
 * properties, and the panel's way of opening a document.
 */

import { AudioLines, Play } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { voiceTone } from '@/components/humint/questioning/recording-format'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { PassageSpeakerName } from './PassageSpeakerName'
import type { PassageDetail, PassageSource, PassageTurn } from './passage-detail'

const PASSAGE = 'investigations.entityDetail.passage'

/** The panel's own micro label (the one over an excerpt's evidence link). */
const MICRO_LABEL = 'text-3xs font-semibold uppercase tracking-wider text-muted-foreground'

/** The panel's quiet surface (a source-document row, an inactive tab). */
const STRIP = 'rounded-md border border-border/40 bg-card/40'

const SPOKEN = 'text-xs leading-relaxed text-foreground/90'

/** Opens a case document over the page; null where no overlay is mounted. */
type OpenDocument = ((documentId: string) => void) | null

export interface PassageDetailSectionProps {
  passage: PassageDetail
  onOpenDocument: OpenDocument
}

interface PassageActionProps {
  source: PassageSource
  startsAt: string | null
  onOpenDocument: OpenDocument
}

/**
 * Open the recording. The page of a HUMINT recording takes a time, so the button names it; a
 * recording that opens as a document does not seek, and the button does not promise it.
 */
function PassageAction({ source, startsAt, onOpenDocument }: PassageActionProps) {
  const { t } = useTranslation()
  if (source.kind === 'recording') {
    return (
      <Button asChild size="xs" className="tabular-nums">
        <Link to={source.to}>
          <Play aria-hidden />
          {startsAt ? t(`${PASSAGE}.openAt`, { time: startsAt }) : t(`${PASSAGE}.open`)}
        </Link>
      </Button>
    )
  }
  return (
    <Button type="button" size="xs" onClick={() => onOpenDocument?.(source.documentId)}>
      <AudioLines aria-hidden />
      {t(`${PASSAGE}.open`)}
    </Button>
  )
}

/** A voice as a chip: its dot in the colour the recording page gives that voice, then who it is. */
function VoiceChip({ voice, children }: { voice: number; children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 self-start rounded-full border border-border/40 bg-background/60 px-1.5 py-px text-3xs font-medium leading-4 text-muted-foreground">
      <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', voiceTone(voice).dot)} />
      {children}
    </span>
  )
}

interface TurnRowProps {
  turn: PassageTurn
  /** The width of the time gutter, in characters: the same for every row. */
  gutter: number
  /** Who speaks; null in a passage where no voice was told apart. */
  who: ReactNode
  language: string | null
}

/**
 * One turn on the rule: its time in the gutter, a tick where it starts, then who speaks and
 * what is said. The rule is the row's own border, so the rows stack into one unbroken line;
 * the time and the tick sit on the middle of the row's first line, a chip or the text.
 */
function TurnRow({ turn, gutter, who, language }: TurnRowProps) {
  return (
    <li className="flex gap-2">
      <time
        dateTime={`PT${turn.startMs / 1000}S`}
        className="shrink-0 pt-2 text-right font-mono tabular-nums text-2xs leading-4 text-muted-foreground"
        style={{ width: `${gutter}ch` }}
      >
        {turn.time}
      </time>
      <div className="relative flex min-w-0 flex-1 flex-col gap-1 border-l border-border py-1.5 pl-2.5 before:absolute before:top-4 before:-left-1.5 before:h-px before:w-1.5 before:bg-border">
        {who !== null && <VoiceChip voice={turn.voice}>{who}</VoiceChip>}
        <p lang={language ?? undefined} className={SPOKEN}>
          {turn.text}
        </p>
      </div>
    </li>
  )
}

/** The turns of a passage, in the recording's order. */
function PassageTurns({ passage, label }: { passage: PassageDetail; label: string }) {
  const { t } = useTranslation()
  const { turns, voiced, language, speakers } = passage
  // A passage that crosses the hour has times of two lengths: the gutter takes the longer.
  const gutter = Math.max(...turns.map(turn => turn.time.length))
  const voiceName = (voice: number) =>
    voice > 0 ? t(`${PASSAGE}.voice`, { number: voice }) : t(`${PASSAGE}.unknownVoice`)
  // Who the recording's page says the voice is; a voice nobody named is its number.
  const whoSpeaks = (voice: number) => {
    const speaker = speakers.get(voice)
    return speaker ? <PassageSpeakerName speaker={speaker} /> : voiceName(voice)
  }
  return (
    <ol aria-label={label} className={cn(STRIP, 'px-2 py-1')}>
      {turns.map(turn => (
        <TurnRow
          key={turn.index}
          turn={turn}
          gutter={gutter}
          who={voiced ? whoSpeaks(turn.voice) : null}
          language={language}
        />
      ))}
    </ol>
  )
}

export function PassageDetailSection({ passage, onOpenDocument }: PassageDetailSectionProps) {
  const { t } = useTranslation()
  const { turns, text, source, startsAt } = passage
  // A document needs the overlay to open in; a recording page is a link and needs nothing.
  const opens = source && (source.kind === 'recording' || onOpenDocument) ? source : null
  if (turns.length === 0 && text === null && !opens) return null
  const label = t(`${PASSAGE}.label`)
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={MICRO_LABEL}>{label}</span>
        {opens && (
          <PassageAction source={opens} startsAt={startsAt} onOpenDocument={onOpenDocument} />
        )}
      </div>
      {turns.length > 0 ? (
        <PassageTurns passage={passage} label={label} />
      ) : text !== null ? (
        // A node whose lists do not line up: its stored lines, one turn each.
        <p
          lang={passage.language ?? undefined}
          className={cn(STRIP, SPOKEN, 'px-2.5 py-2 whitespace-pre-line')}
        >
          {text}
        </p>
      ) : null}
    </section>
  )
}
